import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CreditCard, LogOut, Mail, Clock } from "lucide-react";
import { PRACTICE_EMAIL } from "../lib/brand";

export default function Paywall() {
  const { user, hasAccess, logout } = useAuth();
  const navigate = useNavigate();
  const [subscribing, setSubscribing] = useState(false);
  const pendingApproval = user?.signup_status === "pending_approval" && !user?.is_admin && !hasAccess;
  const denied = user?.signup_status === "denied";

  useEffect(() => {
    if (hasAccess || user?.is_admin) navigate("/dashboard", { replace: true });
  }, [hasAccess, user, navigate]);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/subscribe/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start subscription");
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone px-6 py-12">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="uppercase-label">{pendingApproval ? "Request submitted" : denied ? "Request not approved" : "Activate access"}</div>
            <h1 className="font-heading text-4xl font-semibold mt-2">Welcome, {user?.name?.split(" ")[0]}.</h1>
            <p className="text-muted-foreground mt-2">
              {pendingApproval
                ? "Thanks for signing up — your access request is awaiting review."
                : denied
                ? "Your request was not approved at this time."
                : "Choose how you'll access the network."}
            </p>
          </div>
          <button data-testid="paywall-logout" onClick={logout} className="text-sm text-muted-foreground hover:text-ink inline-flex items-center gap-2">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {pendingApproval ? (
          <div className="module-card p-10 bg-white" data-testid="paywall-pending-card">
            <div className="w-12 h-12 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
              <Clock size={22} />
            </div>
            <div className="uppercase-label">Awaiting admin approval</div>
            <h2 className="font-heading text-2xl font-semibold mt-2">Hang tight — we'll email you shortly.</h2>
            <p className="text-sm text-muted-foreground mt-3">
              Every new account on GCOMS.RN is reviewed by hand so the network stays trusted. You'll get an email at <span className="font-mono">{user?.email}</span> the moment your access is switched on — usually within one business day.
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Already a referring doctor and need instant access? Email us and we'll approve immediately.
            </p>
            <a
              href={`mailto:${PRACTICE_EMAIL}?subject=GCOMS.RN%20access%20request&body=Hi%2C%20please%20approve%20my%20access%20request%20(${encodeURIComponent(user?.email || "")}).%20Thank%20you.`}
              data-testid="paywall-contact-link"
              className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-md bg-ink text-bone text-sm font-medium hover:bg-ink/90"
            >
              <Mail size={16} /> Email {PRACTICE_EMAIL}
            </a>
          </div>
        ) : denied ? (
          <div className="module-card p-10 bg-white" data-testid="paywall-denied-card">
            <div className="uppercase-label">Not approved</div>
            <h2 className="font-heading text-2xl font-semibold mt-2">Your request wasn't approved.</h2>
            <p className="text-sm text-muted-foreground mt-3">
              If you think this is a mistake or you'd like to re-apply with additional details, please contact the practice directly.
            </p>
            <a
              href={`mailto:${PRACTICE_EMAIL}?subject=GCOMS.RN%20access%20appeal&body=Hi%2C%20I'd%20like%20to%20discuss%20my%20access%20request%20(${encodeURIComponent(user?.email || "")}).%20Thank%20you.`}
              className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-md bg-ink text-bone text-sm font-medium hover:bg-ink/90"
            >
              <Mail size={16} /> Email {PRACTICE_EMAIL}
            </a>
          </div>
        ) : (
        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {/* Referring doctor — manual grant */}
          <div className="module-card p-10 bg-white" data-testid="paywall-referring-card">
            <div className="w-12 h-12 rounded-md bg-sage/15 text-sage-deep flex items-center justify-center mb-4">
              <Mail size={22} />
            </div>
            <div className="uppercase-label">Referring doctor?</div>
            <h2 className="font-heading text-2xl font-semibold mt-2">We'll activate you on our end.</h2>
            <p className="text-sm text-muted-foreground mt-3">
              If you're an active referring doctor, contact the practice and we'll switch your account on.
              No codes to remember — your access stays active automatically.
            </p>
            <a
              href={`mailto:${PRACTICE_EMAIL}?subject=GCOMS.RN%20access%20request&body=Hi%2C%20please%20activate%20my%20referring-doctor%20account%20(${encodeURIComponent(user?.email || "")}).%20Thank%20you.`}
              data-testid="paywall-contact-link"
              className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-md bg-sage-deep text-white text-sm font-medium hover:bg-sage-deep/90"
            >
              <Mail size={16} /> Email {PRACTICE_EMAIL}
            </a>
            <div className="mt-4 text-xs text-muted-foreground font-mono break-all">
              Signed up as: {user?.email}
            </div>
          </div>

          {/* Subscribe */}
          <div className="module-card p-10 bg-white">
            <div className="w-12 h-12 rounded-md bg-terracotta/15 text-terracotta-deep flex items-center justify-center mb-4">
              <CreditCard size={22} />
            </div>
            <div className="uppercase-label">Not a referrer yet?</div>
            <h2 className="font-heading text-2xl font-semibold mt-2">Subscribe monthly</h2>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-heading text-4xl font-bold">$99</span>
              <span className="text-sm text-muted-foreground">/30 days</span>
            </div>
            <ul className="mt-4 text-sm text-muted-foreground space-y-1.5">
              <li>· Full access to every module</li>
              <li>· Cancel anytime — no auto-renew lock-in</li>
              <li>· Switch to free access once you're a referring doctor</li>
            </ul>
            <button
              data-testid="paywall-subscribe-button"
              onClick={subscribe}
              disabled={subscribing}
              className="w-full mt-6 py-3 rounded-md bg-terracotta-deep text-white font-medium hover:bg-terracotta-deep/90 disabled:opacity-60"
            >
              {subscribing ? "Redirecting to Stripe..." : "Subscribe via Stripe"}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
